// SGNL-230 — Revenue Calendar tab.
//
// Top-level tab driven by the SGNL-229 backend (3 endpoints under
// /api/revenue-projection). Renders:
//   - Top bar: year picker + threshold chips (80/90/95/99 multi-select).
//   - KPI cards: ITB / High-Prob / Total (reactive to chip selection).
//   - BU breakdown: stacked bar via drawSortableStacked (ITB + one stack
//     per selected high-prob bucket).
//   - Drill-through modal: click a BU → opp list → 12-month chart.
//   - Delta panel (collapsible): two date pickers + chips + per-month
//     delta chart + "what moved" table.
//
// Naming: this is "Revenue Calendar", deliberately distinct from
// SGNL-213's "Revenue Projection" sub-view inside Pipeline Analytics
// (probability-weighted pipeline math). See
// docs/sgnl-229-revenue-projection.md for the design and the BE
// contract — including that buckets are MUTUALLY EXCLUSIVE.

import { Chart } from "chart.js/auto";

import {
  ApiError,
  fetchOpportunityMonthlyRevenue,
  fetchRevenuePacing,
  fetchRevenueProjection,
  fetchRevenueProjectionDelta,
  fetchRevenueProjectionOpportunities,
} from "../api";
import { esc, fmtMoney } from "../lib/format";
import { setActiveTab, setFilters } from "../state";
import type {
  AISplit,
  HighProbBucket,
  RepAIClass,
  MonthlyRevenueResponse,
  OpportunityYearRow,
  RevenuePacingResponse,
  RevenueProjectionDeltaResponse,
  RevenueProjectionResponse,
} from "../types";
import { drawSortableStacked } from "./charts";

// ---------------------------------------------------------------------------
// Module-level state. Tab-scoped, not in app state.ts (cross-tab state
// concerns: chip selection for *this* tab is internal and doesn't need
// to be globally observable).
// ---------------------------------------------------------------------------

const ALLOWED_THRESHOLDS: readonly number[] = [80, 90, 95, 99] as const;

// Whitelist mirrors the backend's `_ALLOWED_THRESHOLDS` set. Keeping a
// local copy so an out-of-range chip click never reaches the BE — the
// BE would 422, but the FE catches it client-side first.
const BACKEND_ALLOWED: ReadonlySet<number> = new Set([50, 60, 70, 80, 90, 95, 99]);

interface CalendarState {
  year: number;
  /** Selected thresholds, deduped + sorted desc to match BE echo. */
  thresholds: number[];
  /** Pass-through revenue toggle — defaults true so the legacy KPIs
   * match the new Pacing Summary at first paint. Operator can uncheck
   * for finance debugging (surfaces External Licenses/Fees, etc.).
   * Not URL-shared today; if a permalink feature lands, this needs
   * to round-trip through the URL query string so a shared link
   * carries the exact filter set the sender saw (ARCH-03 pre-PR). */
  excludePassThrough: boolean;
  projection: RevenueProjectionResponse | null;
  projectionError: string | null;
  loading: boolean;
  /** Delta panel: collapsed by default; expands lazily. */
  deltaExpanded: boolean;
  deltaFrom: string;
  deltaTo: string;
  deltaResult: RevenueProjectionDeltaResponse | null;
  deltaError: string | null;
  deltaLoading: boolean;
  /** SGNL-246 — 3-bucket pacing summary, independent of the legacy
   * threshold-driven projection. Refreshed in parallel on year change. */
  pacing: RevenuePacingResponse | null;
  pacingError: string | null;
  pacingLoading: boolean;
}

function _defaultYear(): number {
  return new Date().getUTCFullYear();
}

function _defaultDeltaTo(): string {
  // Today, ISO date.
  return new Date().toISOString().slice(0, 10);
}

function _defaultDeltaFrom(): string {
  // 30 days ago — small enough span that the BE's 366-day cap never
  // fires on the default open of the panel.
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 30);
  return d.toISOString().slice(0, 10);
}

const state: CalendarState = {
  year: _defaultYear(),
  thresholds: [],
  excludePassThrough: true,
  projection: null,
  projectionError: null,
  loading: false,
  deltaExpanded: false,
  deltaFrom: _defaultDeltaFrom(),
  deltaTo: _defaultDeltaTo(),
  deltaResult: null,
  deltaError: null,
  deltaLoading: false,
  pacing: null,
  pacingError: null,
  pacingLoading: false,
};

/**
 * Test seam — reset the entire module state. Mirror of the pattern used
 * by SGNL-213 (`_resetRevenueProjectionState`). Vitest beforeEach hooks
 * call this so each test sees a fresh module.
 */
export function _resetRevenueCalendarState(): void {
  state.year = _defaultYear();
  state.thresholds = [];
  state.excludePassThrough = true;
  state.projection = null;
  state.projectionError = null;
  state.loading = false;
  state.deltaExpanded = false;
  state.deltaFrom = _defaultDeltaFrom();
  state.deltaTo = _defaultDeltaTo();
  state.deltaResult = null;
  state.deltaError = null;
  state.deltaLoading = false;
  state.pacing = null;
  state.pacingError = null;
  state.pacingLoading = false;
}

// Tracker for the BE-issued monotonic fetch sequence (PR-Agent #145
// pattern): if the operator changes chips/year quickly, only the latest
// in-flight fetch is allowed to commit its response.
let _fetchSeq = 0;

// Separate sequence for the delta panel — pre-PR architect MED: rapid
// "Compute delta" clicks or a year-change-while-in-flight would
// otherwise let a stale delta response overwrite the latest result.
let _deltaSeq = 0;

// Third monotonic counter for the opp-monthly drill lookup —
// PR-Agent #147 (importance 6): if an operator submits two opp IDs
// in quick succession, the slower lookup would otherwise render
// into the modal after the faster one's chart was already drawn,
// showing the wrong opp's data. The `isConnected` check inside
// `_loadOppMonthly` additionally prevents rendering into a
// torn-down modal (drill closed mid-flight).
let _oppMonthlySeq = 0;

// Chart instance handles owned by the view (separate from the
// charts.ts registry which uses canvas IDs — these are inline canvases
// inside the drill modal + delta panel, so we destroy them manually).
let _drillChart: Chart | null = null;
let _deltaChart: Chart | null = null;

// Pre-PR architect HIGH + code-review HIGH + security LOW:
// `document.addEventListener("keydown", ...)` and the host-level
// click listener for the drill backdrop both leak across tab re-mounts
// because `renderRevenueCalendarTab` is called every time the operator
// switches back to this tab. We tie both listeners to an
// AbortController whose `.abort()` is called at the top of every
// re-mount, so the prior generation's listeners are torn down before
// new ones are attached. Idiomatic teardown that doesn't require a
// separate exported `unmountRevenueCalendarTab`.
let _bindController: AbortController | null = null;

// ---------------------------------------------------------------------------
// Public renderer entry point — called from main.ts TAB_RENDERERS.
// ---------------------------------------------------------------------------

export async function renderRevenueCalendarTab(host: HTMLElement): Promise<void> {
  // Pre-PR HIGH (architect + code-review): tear down the prior
  // generation of document-level listeners + chart instances BEFORE
  // re-mounting. Calling `renderRevenueCalendarTab` again (e.g. on
  // tab re-switch) would otherwise stack a fresh Escape listener
  // and host click listener on top of the old ones, plus leak
  // Chart.js animation frame loops if either chart is still open.
  _bindController?.abort();
  _bindController = new AbortController();
  _drillChart?.destroy();
  _drillChart = null;
  _deltaChart?.destroy();
  _deltaChart = null;

  // Boot UI shell once. Subsequent re-renders only touch sub-regions so
  // the year picker / chips don't blink on each refetch.
  host.innerHTML = `
    <section class="revenue-calendar" data-testid="revenue-calendar">
      <header class="revenue-calendar__header">
        <h2 class="revenue-calendar__title">Revenue Calendar</h2>
      </header>
      <!-- SGNL-246: 3-bucket pacing summary, calls the new
           /api/revenue-pacing endpoint. Lives at the TOP so the
           corrected numbers are the first thing the user sees. -->
      <div class="revenue-calendar__pacing" data-testid="rc-pacing"></div>
      <div class="revenue-calendar__topbar" data-testid="rc-topbar"></div>
      <div class="revenue-calendar__kpis" data-testid="rc-kpis"></div>
      <div class="revenue-calendar__bu" data-testid="rc-bu"></div>
      <div class="revenue-calendar__delta" data-testid="rc-delta"></div>
      <div class="revenue-calendar__drill" data-testid="rc-drill" hidden></div>
    </section>
  `;
  _bindTopBar(host);
  _bindDeltaPanel(host);
  _bindDrillBackdropClose(host);
  // Fetch the new pacing endpoint in parallel with the legacy projection
  // — neither blocks the other; both refresh independently on year change.
  await Promise.all([_refreshProjection(host), _refreshPacing(host)]);
}

// ---------------------------------------------------------------------------
// Top bar — year picker + threshold chips
// ---------------------------------------------------------------------------

function _bindTopBar(host: HTMLElement): void {
  const topbar = host.querySelector<HTMLElement>("[data-testid='rc-topbar']");
  if (!topbar) return;
  topbar.innerHTML = _topBarHTML();

  const yearInput = topbar.querySelector<HTMLInputElement>("[data-testid='rc-year']");
  yearInput?.addEventListener("change", () => {
    const v = Number(yearInput.value);
    // PR-Agent #147 (importance 5): `Number.isFinite(2026.5)` is
    // true and would have committed a non-integer to state, which
    // the BE then 422s. Use `Number.isInteger` so the year is
    // structurally an integer before we fire any refetch.
    if (!Number.isInteger(v) || v < 2000 || v > 2100) {
      // Snap back — don't send invalid years to the BE.
      yearInput.value = String(state.year);
      return;
    }
    if (v === state.year) return;
    state.year = v;
    void _refreshProjection(host);
    // SGNL-246: keep the new pacing summary in sync with year changes
    void _refreshPacing(host);
    if (state.deltaExpanded) {
      void _refreshDelta(host);
    }
  });

  topbar.querySelectorAll<HTMLButtonElement>("[data-threshold]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const raw = Number(btn.dataset.threshold);
      if (!BACKEND_ALLOWED.has(raw)) return;
      const idx = state.thresholds.indexOf(raw);
      if (idx >= 0) state.thresholds.splice(idx, 1);
      else state.thresholds.push(raw);
      state.thresholds.sort((a, b) => b - a);
      btn.classList.toggle("rc-chip--on", state.thresholds.includes(raw));
      btn.setAttribute("aria-pressed", state.thresholds.includes(raw) ? "true" : "false");
      void _refreshProjection(host);
      // PR-Agent #164 importance 8: ANY threshold change (not just
      // toggle-to-empty) invalidates a prior delta — it was
      // computed for a different high-prob band. Clear cached
      // result/error/loading and bump the monotonic seq so an
      // in-flight response from the prior band can't commit. The
      // subsequent _refreshDelta below picks up a fresh seq.
      state.deltaResult = null;
      state.deltaError = null;
      state.deltaLoading = false;
      ++_deltaSeq;
      // Delta panel button enabled-state + hint visibility depend
      // on this set — re-render the shell so toggling chips
      // immediately reflects. When the panel is open with non-empty
      // thresholds, the user expects the delta to recompute against
      // the new band (mirrors year + pass-through toggle behaviour).
      _renderDeltaShell(host);
      if (state.deltaExpanded && state.thresholds.length > 0) {
        void _refreshDelta(host);
      }
    });
  });

  // Pass-through exclusion toggle — refetches the legacy projection +
  // delta (if expanded) with the new flag. The Pacing Summary above is
  // ALWAYS exclusion-filtered (SGNL-245 contract), so it doesn't refire.
  // Read `checked` from the event target so the handler doesn't hold a
  // reference to a possibly-stale captured element (the topbar's
  // innerHTML is rewritten on each render).
  const checkbox = topbar.querySelector<HTMLInputElement>("[data-testid='rc-exclude-checkbox']");
  if (checkbox) {
    checkbox.addEventListener("change", (e) => {
      state.excludePassThrough = (e.currentTarget as HTMLInputElement).checked;
      void _refreshProjection(host);
      if (state.deltaExpanded) {
        void _refreshDelta(host);
      }
    });
  }
}

function _topBarHTML(): string {
  const chips = ALLOWED_THRESHOLDS.map((t) => {
    const on = state.thresholds.includes(t);
    return `<button type="button"
      class="rc-chip${on ? " rc-chip--on" : ""}"
      data-threshold="${t}"
      data-testid="rc-chip-${t}"
      aria-pressed="${on ? "true" : "false"}">${t}%+</button>`;
  }).join("");
  return `
    <label class="rc-year">Year
      <input
        type="number"
        min="2000"
        max="2100"
        value="${state.year}"
        data-testid="rc-year"
      />
    </label>
    <fieldset class="rc-chips">
      <legend class="rc-chips__legend">High-prob buckets (exclusive)</legend>
      ${chips}
    </fieldset>
    <label class="rc-exclude-toggle" data-testid="rc-exclude-toggle">
      <input
        type="checkbox"
        ${state.excludePassThrough ? "checked" : ""}
        data-testid="rc-exclude-checkbox"
      />
      <span>Exclude pass-through revenue</span>
    </label>
  `;
}

// ---------------------------------------------------------------------------
// Projection fetch + KPI / BU render
// ---------------------------------------------------------------------------

async function _refreshProjection(host: HTMLElement): Promise<void> {
  const seq = ++_fetchSeq;
  state.loading = true;
  state.projectionError = null;
  _renderKPIs(host);
  _renderBU(host);
  try {
    const r = await fetchRevenueProjection({
      year: state.year,
      thresholds: state.thresholds,
      excludePassThrough: state.excludePassThrough,
    });
    if (seq !== _fetchSeq) return; // stale; ignore
    state.projection = r;
    state.loading = false;
  } catch (e) {
    if (seq !== _fetchSeq) return;
    state.projection = null;
    state.loading = false;
    state.projectionError = e instanceof Error ? e.message : String(e);
  }
  _renderKPIs(host);
  _renderBU(host);
}

function _renderKPIs(host: HTMLElement): void {
  const target = host.querySelector<HTMLElement>("[data-testid='rc-kpis']");
  if (!target) return;
  if (state.loading) {
    target.innerHTML = `<div class="rc-card rc-card--loading">Loading…</div>`;
    return;
  }
  if (state.projectionError) {
    target.innerHTML = `<div class="rc-card rc-card--error">Failed to load: ${esc(state.projectionError)}</div>`;
    return;
  }
  const p = state.projection;
  if (!p) {
    target.innerHTML = "";
    return;
  }
  const itb = p.itb.total_amount;
  // SGNL-266 — derive the High-Prob headline from the server AI split (the
  // source of truth established for the drill cells) so the headline can never
  // diverge from the sum of its Selling/Using/Non cells. Falls back to the
  // per-bucket sum when the split is absent (deploy skew / older API).
  const hpTotal = p.high_prob.ai_split
    ? p.high_prob.ai_split.selling_amount +
      p.high_prob.ai_split.using_amount +
      p.high_prob.ai_split.non_ai_amount
    : p.high_prob.by_threshold.reduce((s, b) => s + b.total_amount, 0);
  const cards: string[] = [];
  cards.push(
    _card("ITB", "rc-card-itb", itb, "Signed contracts recognizing this year", p.itb.ai_split),
  );
  if (state.thresholds.length > 0) {
    cards.push(
      _card(
        "High-Prob",
        "rc-card-hp",
        hpTotal,
        "Probability-weighted across selected buckets",
        p.high_prob.ai_split,
      ),
    );
    for (const b of p.high_prob.by_threshold) {
      cards.push(_bucketCard(b));
    }
  }
  cards.push(
    _card("Total", "rc-card-total", p.total_projection, "ITB + selected buckets", p.total_ai_split),
  );
  target.innerHTML = cards.join("");
  // SGNL-266 — AI-class drill: clicking a Selling/Using/Non figure sets the
  // top-level AI filter and jumps to the opp list, identical to the AI
  // Breakdown tab's hyperlink drill (setFilters + setActiveTab).
  for (const a of target.querySelectorAll<HTMLAnchorElement>("a.drill-cell")) {
    a.addEventListener("click", (e) => {
      e.preventDefault();
      // Values are our own static "selling"|"using"|"non" (RepAIClass) — the
      // narrow cast keeps a future LLM-class value from silently mis-routing.
      const cls = a.dataset.aiClass as RepAIClass | undefined;
      if (cls) setFilters({ aiClass: cls });
      setActiveTab("deals");
    });
  }
}

// SGNL-266 — three drill-cell links (Selling / Using / Non-AI) mirroring the
// AI Breakdown tab: href="#deals" + data-ai-class so the shared drill sets the
// top-level AI filter and navigates to the opp list.
function _aiDrill(split: AISplit): string {
  const cell = (cls: RepAIClass, label: string, amt: number): string =>
    `<a href="#deals" class="drill-cell rc-card__ai-cell" data-ai-class="${esc(cls)}"` +
    ` title="See ${esc(label)} deals">${esc(label)}: ${esc(fmtMoney(amt))}</a>`;
  return `<div class="rc-card__ai" data-testid="rc-ai-split">
      ${cell("selling", "Selling AI", split.selling_amount)}
      ${cell("using", "Using AI", split.using_amount)}
      ${cell("non", "Non-AI", split.non_ai_amount)}
    </div>`;
}

function _card(
  label: string,
  testid: string,
  amount: number,
  sub: string,
  aiSplit?: AISplit,
): string {
  return `
    <div class="rc-card" data-testid="${testid}">
      <div class="rc-card__label">${esc(label)}</div>
      <div class="rc-card__amount">${esc(fmtMoney(amount))}</div>
      <div class="rc-card__sub">${esc(sub)}</div>
      ${aiSplit ? _aiDrill(aiSplit) : ""}
    </div>
  `;
}

function _bucketCard(b: HighProbBucket): string {
  // Pre-PR security MED + code-review MED: defensively `esc()` even
  // numeric fields. `b.threshold` and `b.by_bu.length` are typed as
  // `number` at the TS layer but the runtime guarantee depends on the
  // BE never emitting a string here — if either ever drifted (e.g. a
  // hand-crafted response in a test fixture or a future schema change
  // sending strings), the `data-testid` attribute would inherit the
  // raw value. Cheap insurance + lint consistency.
  return `
    <div class="rc-card rc-card--bucket" data-testid="rc-bucket-${esc(b.threshold)}">
      <div class="rc-card__label">≥${esc(b.threshold)}% bucket</div>
      <div class="rc-card__amount">${esc(fmtMoney(b.total_amount))}</div>
      <div class="rc-card__sub">${esc(b.by_bu.length)} BU${b.by_bu.length === 1 ? "" : "s"}</div>
      ${b.ai_split ? _aiDrill(b.ai_split) : ""}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// BU breakdown — stacked bar (ITB + one stack per bucket) via
// drawSortableStacked. Click → drill-through.
// ---------------------------------------------------------------------------

interface BURow {
  bu: string;
  itb: number;
  // Dynamic per-threshold keys (hp_99, hp_95, hp_90, hp_80). The
  // drawSortableStacked helper accepts arbitrary keys via the
  // `SortableSeries` shape.
  [k: string]: number | string;
}

function _renderBU(host: HTMLElement): void {
  const target = host.querySelector<HTMLElement>("[data-testid='rc-bu']");
  if (!target) return;
  if (state.loading) {
    target.innerHTML = `<div class="rc-bu__empty">Loading BU breakdown…</div>`;
    return;
  }
  if (state.projectionError) {
    target.innerHTML = "";
    return;
  }
  const p = state.projection;
  if (!p) {
    target.innerHTML = "";
    return;
  }

  const rows = _buildBURows(p);
  if (rows.length === 0) {
    target.innerHTML = `
      <div class="rc-bu__empty" data-testid="rc-bu-empty">
        No BU breakdown for ${esc(String(state.year))} with the current selection.
      </div>
    `;
    return;
  }

  // Mount a fresh canvas — drawSortableStacked destroys + recreates by
  // canvas ID, but we want the rest of the breakdown shell (drill list
  // below) to re-render predictably, so we rebuild the whole region.
  const series: { label: string; key: keyof BURow; color: string }[] = [
    { label: "ITB", key: "itb", color: "#2bb673" }, // bounteous-green
  ];
  // Order series in the same descending-threshold order the BE returns
  // — visually the highest-confidence stack sits adjacent to ITB.
  for (const b of p.high_prob.by_threshold) {
    series.push({
      label: `≥${b.threshold}%`,
      key: `hp_${b.threshold}` as keyof BURow,
      color: _bucketColor(b.threshold),
    });
  }

  // SGNL-232 — table FIRST, chart SECOND. User feedback on SGNL-230
  // ship: "by business unit is okay. You should show some numbers
  // also, so we know it." The drill table already carries the numbers;
  // promoting it above the chart means numbers read at-a-glance
  // without scrolling past the visualization. Chart remains as the
  // visual summary below.
  //
  // PR-Agent #148 (importance 2): the HTML structure puts the table
  // above the chart visually, but we initialise the chart FIRST
  // before calling `_renderBUDrillList` so any future change to the
  // drill list that reads chart-derived DOM state (e.g. legend
  // toggles) doesn't observe an uninitialised canvas. Visual order
  // is set by markup; init order is set by JS — keep them
  // independent.
  target.innerHTML = `
    <h3 class="rc-bu__title">By Business Unit</h3>
    <div class="rc-bu__drill" data-testid="rc-bu-drill"></div>
    <div class="rc-bu__chart-wrap">
      <canvas id="rc-bu-chart" data-testid="rc-bu-chart" aria-label="Revenue by BU stacked bar"></canvas>
    </div>
  `;
  drawSortableStacked<BURow>("rc-bu-chart", rows, "bu", series, `Revenue by BU — ${state.year}`);
  _renderBUDrillList(host, rows);
}

function _buildBURows(p: RevenueProjectionResponse): BURow[] {
  // Pivot: union of every BU mentioned across ITB and bucket sections.
  const bus = new Set<string>();
  const itbByBu = new Map<string, number>();
  for (const e of p.itb.by_bu) {
    const key = e.business_unit ?? "(unset)";
    bus.add(key);
    itbByBu.set(key, e.amount);
  }
  const bucketMaps: { threshold: number; byBu: Map<string, number> }[] = [];
  for (const b of p.high_prob.by_threshold) {
    const m = new Map<string, number>();
    for (const e of b.by_bu) {
      const key = e.business_unit ?? "(unset)";
      bus.add(key);
      m.set(key, e.amount);
    }
    bucketMaps.push({ threshold: b.threshold, byBu: m });
  }
  const rows: BURow[] = [];
  for (const bu of bus) {
    const row: BURow = { bu, itb: itbByBu.get(bu) ?? 0 };
    for (const bm of bucketMaps) {
      row[`hp_${bm.threshold}`] = bm.byBu.get(bu) ?? 0;
    }
    rows.push(row);
  }
  return rows;
}

const _BUCKET_PALETTE: Record<number, string> = {
  99: "#5b34c8", // bounteous-purple-deep
  95: "#7b61ff",
  90: "#a98bff",
  80: "#cdb8ff",
  70: "#e3d6ff",
  60: "#efe5ff",
  50: "#f7f0ff",
};
function _bucketColor(threshold: number): string {
  return _BUCKET_PALETTE[threshold] ?? "#999999";
}

// ---------------------------------------------------------------------------
// Drill-through: BU stack → opp list → 12-month chart
// ---------------------------------------------------------------------------

function _renderBUDrillList(host: HTMLElement, rows: BURow[]): void {
  const target = host.querySelector<HTMLElement>("[data-testid='rc-bu-drill']");
  if (!target) return;
  if (rows.length === 0) {
    target.innerHTML = "";
    return;
  }
  const sorted = rows.slice().sort((a, b) => {
    const sa = _rowTotal(a);
    const sb = _rowTotal(b);
    return sb - sa;
  });
  target.innerHTML = `
    <h4 class="rc-bu-drill__title">BU totals</h4>
    <table class="rc-bu-drill__table" data-testid="rc-bu-drill-table">
      <thead>
        <tr>
          <th scope="col">Business Unit</th>
          <th scope="col" class="rc-num">ITB</th>
          ${
            state.projection?.high_prob.by_threshold
              .map((b) => `<th scope="col" class="rc-num">≥${b.threshold}%</th>`)
              .join("") ?? ""
          }
          <th scope="col" class="rc-num">Total</th>
        </tr>
      </thead>
      <tbody>
        ${sorted
          .map((r) => {
            const total = _rowTotal(r);
            const cells = [
              `<td>${esc(r.bu)}</td>`,
              `<td class="rc-num">${esc(fmtMoney(r.itb as number))}</td>`,
              ...(state.projection?.high_prob.by_threshold ?? []).map(
                (b) =>
                  `<td class="rc-num">${esc(fmtMoney((r[`hp_${b.threshold}`] as number) ?? 0))}</td>`,
              ),
              `<td class="rc-num"><strong>${esc(fmtMoney(total))}</strong></td>`,
            ];
            return `<tr data-bu="${esc(r.bu)}">${cells.join("")}</tr>`;
          })
          .join("")}
      </tbody>
    </table>
  `;
  // No opp-drill click handler yet — wired below via the BU row → opp
  // list flow. v1 ships the BU table; the opp-monthly chart is reached
  // via the chart click / table row click (see _bindOppDrill).
  _bindBUDrillClicks(host, target);
}

function _rowTotal(r: BURow): number {
  let s = r.itb as number;
  for (const k of Object.keys(r)) {
    if (k.startsWith("hp_")) s += r[k] as number;
  }
  return s;
}

function _bindBUDrillClicks(host: HTMLElement, table: HTMLElement): void {
  table.addEventListener("click", (ev) => {
    const target = ev.target;
    if (!(target instanceof HTMLElement)) return;
    const tr = target.closest<HTMLTableRowElement>("tr[data-bu]");
    if (!tr) return;
    const bu = tr.dataset.bu;
    if (!bu) return;
    _openBUOppListDrill(host, bu);
  });
}

/**
 * SGNL-233 — Drill stage 1: opportunity list for a BU + year.
 *
 * Replaces the SGNL-230 v1 opp-ID lookup form. The BE endpoint
 * `/api/revenue-projection/opportunities` returns opps for the BU
 * already excluded of Closed Lost / sub-pipeline-min, ordered by
 * weighted_year_total descending. We render them as clickable rows;
 * clicking flows into the existing `_loadOppMonthly` 12-month chart.
 *
 * Empty list (e.g. scoped-out BU, or no qualifying opps in that year)
 * surfaces as an empty-state message instead of the lookup form so
 * users aren't tempted to type random IDs.
 */
function _openBUOppListDrill(host: HTMLElement, bu: string): void {
  const drill = host.querySelector<HTMLElement>("[data-testid='rc-drill']");
  if (!drill) return;
  drill.hidden = false;
  drill.innerHTML = `
    <div class="rc-drill-backdrop" data-testid="rc-drill-backdrop">
      <div role="dialog" aria-modal="true" aria-label="BU drill"
           class="rc-drill-modal" data-testid="rc-drill-modal">
        <header class="rc-drill-modal__header">
          <h3>${esc(bu)} — ${esc(String(state.year))}</h3>
          <button type="button" class="rc-drill-modal__close"
                  data-testid="rc-drill-close" aria-label="Close drill">×</button>
        </header>
        <div class="rc-drill-modal__body">
          <div class="rc-opp-list" data-testid="rc-opp-list">
            <div class="rc-opp-list__loading">Loading opportunities…</div>
          </div>
          <div class="rc-opp-monthly" data-testid="rc-opp-monthly"></div>
        </div>
      </div>
    </div>
  `;
  const close = drill.querySelector<HTMLButtonElement>("[data-testid='rc-drill-close']");
  close?.addEventListener("click", () => _closeDrill(host));
  // BU label "(unset)" comes from the FE pivot; map back to null when
  // calling the BE so the BU=NULL bucket is queried.
  const buArg = bu === "(unset)" ? null : bu;
  void _loadOppList(host, buArg);
}

// Fourth monotonic counter for the opp-list fetch — mirrors the
// `_oppMonthlySeq` pattern. Rapid drill opens (click BU, close,
// click another BU) would otherwise let a stale list render into
// the newly-opened modal. The `target.isConnected` check on the
// render path additionally guards against rendering into a torn-down
// modal.
let _oppListSeq = 0;

async function _loadOppList(host: HTMLElement, buArg: string | null): Promise<void> {
  const seq = ++_oppListSeq;
  const target = host.querySelector<HTMLElement>("[data-testid='rc-opp-list']");
  if (!target) return;
  try {
    const r = await fetchRevenueProjectionOpportunities({
      year: state.year,
      businessUnit: buArg,
      excludePassThrough: state.excludePassThrough,
    });
    if (seq !== _oppListSeq || !target.isConnected) return;
    _renderOppList(host, r.opportunities);
  } catch (e) {
    if (seq !== _oppListSeq || !target.isConnected) return;
    // Pre-PR SGNL-233 code-review CR-07: escape the dynamic part
    // only — concatenating before esc() is harmless here but
    // diverges from the `_loadOppMonthly` pattern that escapes
    // each interpolation site individually. Keep the static prefix
    // unescaped so the inline template literal reads naturally.
    const msg = e instanceof Error ? e.message : String(e);
    target.innerHTML = `
      <div class="rc-opp-list__error" data-testid="rc-opp-list-error">
        Failed to load opportunities: ${esc(msg)}
      </div>
    `;
  }
}

function _renderOppList(host: HTMLElement, opps: readonly OpportunityYearRow[]): void {
  const target = host.querySelector<HTMLElement>("[data-testid='rc-opp-list']");
  if (!target) return;
  if (opps.length === 0) {
    target.innerHTML = `
      <div class="rc-opp-list__empty" data-testid="rc-opp-list-empty">
        No opportunities contribute to this BU for ${esc(String(state.year))}.
      </div>
    `;
    return;
  }
  // Server already orders by weighted_year_total desc — preserve.
  const rows = opps
    .map(
      (o) => `
      <tr data-opp="${esc(o.opportunity_id)}" class="rc-opp-row">
        <td>${esc(o.name)}</td>
        <td>${esc(o.stage ?? "—")}</td>
        <td class="rc-num">${esc(o.probability == null ? "—" : `${o.probability}%`)}</td>
        <td class="rc-num">${esc(fmtMoney(o.year_total))}</td>
        <td class="rc-num"><strong>${esc(fmtMoney(o.weighted_year_total))}</strong></td>
      </tr>`,
    )
    .join("");
  target.innerHTML = `
    <table class="rc-opp-list__table" data-testid="rc-opp-list-table">
      <thead>
        <tr>
          <th scope="col">Opportunity</th>
          <th scope="col">Stage</th>
          <th scope="col" class="rc-num">Prob</th>
          <th scope="col" class="rc-num">Year total</th>
          <th scope="col" class="rc-num">Weighted</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;
  target.querySelectorAll<HTMLTableRowElement>("tr[data-opp]").forEach((tr) => {
    tr.addEventListener("click", () => {
      const oppId = tr.dataset.opp;
      if (oppId) void _loadOppMonthly(host, oppId);
    });
  });
}

function _bindDrillBackdropClose(host: HTMLElement): void {
  // Pre-PR architect HIGH + code-review HIGH: both listeners are
  // attached with the module-level AbortController's signal, so
  // re-mounting `renderRevenueCalendarTab` tears them down via
  // `_bindController.abort()` before the next mount re-binds. Without
  // this, every tab re-visit stacks another live listener on the
  // host + document.
  const signal = _bindController?.signal;

  // Backdrop click closes the modal. Wire on the host so the
  // listener survives multiple drill open/close cycles within a
  // single mount lifetime.
  host.addEventListener(
    "click",
    (ev) => {
      const target = ev.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.dataset.testid === "rc-drill-backdrop") {
        _closeDrill(host);
      }
    },
    { signal },
  );
  // Esc closes the drill when open. Gated on the drill being visible
  // so the listener is a no-op outside drill mode.
  document.addEventListener(
    "keydown",
    (ev) => {
      if (ev.key !== "Escape") return;
      const drill = host.querySelector<HTMLElement>("[data-testid='rc-drill']");
      if (!drill || drill.hidden) return;
      _closeDrill(host);
    },
    { signal },
  );
}

function _closeDrill(host: HTMLElement): void {
  const drill = host.querySelector<HTMLElement>("[data-testid='rc-drill']");
  if (!drill) return;
  drill.hidden = true;
  drill.innerHTML = "";
  _drillChart?.destroy();
  _drillChart = null;
}

async function _loadOppMonthly(host: HTMLElement, oppId: string): Promise<void> {
  // PR-Agent #147 (importance 6): claim a sequence number BEFORE
  // the fetch fires so two concurrent lookups (operator submits
  // two opp IDs quickly) can be ordered. Same pattern as
  // `_fetchSeq` and `_deltaSeq`.
  const seq = ++_oppMonthlySeq;
  const target = host.querySelector<HTMLElement>("[data-testid='rc-opp-monthly']");
  if (!target) return;
  target.innerHTML = `<div class="rc-opp-monthly__loading">Loading…</div>`;
  try {
    const m = await fetchOpportunityMonthlyRevenue(oppId, { year: state.year });
    // Race + lifecycle guards: drop the result if a newer lookup
    // has been kicked off, OR if the modal was torn down while the
    // fetch was in flight (target detached from DOM).
    if (seq !== _oppMonthlySeq || !target.isConnected) return;
    target.innerHTML = `
      <div class="rc-opp-monthly__summary">
        <strong>${esc(oppId)}</strong> · ${esc(String(state.year))} total
        <span data-testid="rc-opp-total">${esc(fmtMoney(m.total))}</span>
      </div>
      <div class="rc-opp-monthly__chart-wrap">
        <canvas id="rc-opp-monthly-chart" data-testid="rc-opp-monthly-chart"
                aria-label="Monthly revenue chart"></canvas>
      </div>
    `;
    _drawOppMonthlyChart(m);
  } catch (e) {
    if (seq !== _oppMonthlySeq || !target.isConnected) return;
    // Pre-PR code-review HIGH: branch on the typed ApiError instead
    // of regex-matching the message text. `msg.includes("404")` would
    // false-positive on any URL whose path segment contained "404"
    // (e.g. a Salesforce ID coincidentally containing those digits)
    // and silently break if the error template ever changes.
    const out404 = e instanceof ApiError && e.status === 404;
    const msg = e instanceof Error ? e.message : String(e);
    target.innerHTML = `
      <div class="rc-opp-monthly__error" data-testid="rc-opp-monthly-error">
        ${esc(out404 ? "Opportunity not found or out of your BU scope." : msg)}
      </div>
    `;
  }
}

function _drawOppMonthlyChart(m: MonthlyRevenueResponse): void {
  const canvas = document.getElementById("rc-opp-monthly-chart");
  if (!(canvas instanceof HTMLCanvasElement)) return;
  _drillChart?.destroy();
  // Pad to 12; the BE already pads, but this defends against unexpected
  // response shapes.
  const byMonth = new Map(m.months.map((x) => [x.month, x.amount]));
  const data = Array.from({ length: 12 }, (_, i) => byMonth.get(i + 1) ?? 0);
  const labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  _drillChart = new Chart(canvas, {
    type: "bar",
    data: { labels, datasets: [{ label: "Monthly revenue", data, backgroundColor: "#7b61ff" }] },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        title: { display: true, text: `Monthly revenue · ${m.year}` },
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (c) => `${c.dataset.label}: ${fmtMoney(c.parsed.y)}`,
          },
        },
      },
      scales: {
        y: {
          ticks: {
            callback: (v) => {
              const n = typeof v === "string" ? Number(v) : v;
              return Number.isFinite(n) ? fmtMoney(n) : String(v);
            },
          },
        },
      },
    },
  });
}

// ---------------------------------------------------------------------------
// Delta panel (collapsible)
// ---------------------------------------------------------------------------

function _bindDeltaPanel(host: HTMLElement): void {
  const target = host.querySelector<HTMLElement>("[data-testid='rc-delta']");
  if (!target) return;
  _renderDeltaShell(host);
}

function _renderDeltaShell(host: HTMLElement): void {
  const target = host.querySelector<HTMLElement>("[data-testid='rc-delta']");
  if (!target) return;
  // The delta engine is defined as movement WITHIN the high-prob band
  // (the threshold chips on the topbar). With no chips selected the
  // BE short-circuits to all-zero deltas — the prior UX silently
  // rendered that as "$0 net movement (ITB unchanged)" which read as
  // "data confirms no movement" instead of "you haven't picked a
  // band to measure against". Disable the Run button + show an
  // inline hint above the controls when thresholds is empty, and
  // surface the same hint as the status if a stale empty-thresholds
  // response is still on screen.
  const noThresholds = state.thresholds.length === 0;
  const hintHtml = noThresholds
    ? `<div class="rc-delta__hint" data-testid="rc-delta-hint">
         Select one or more threshold chips above (80%+, 90%+, 95%+, 99%+) to pick the band this delta measures.
       </div>`
    : "";
  target.innerHTML = `
    <button type="button"
            class="rc-delta__toggle"
            data-testid="rc-delta-toggle"
            aria-expanded="${state.deltaExpanded ? "true" : "false"}">
      ${state.deltaExpanded ? "▾" : "▸"} Compare snapshot dates
    </button>
    <div class="rc-delta__body" data-testid="rc-delta-body" ${state.deltaExpanded ? "" : "hidden"}>
      ${hintHtml}
      <div class="rc-delta__controls">
        <label>From
          <input type="date" data-testid="rc-delta-from" value="${esc(state.deltaFrom)}" />
        </label>
        <label>To
          <input type="date" data-testid="rc-delta-to" value="${esc(state.deltaTo)}" />
        </label>
        <button type="button" data-testid="rc-delta-run" class="rc-delta__run"
                ${noThresholds ? "disabled" : ""}
                aria-disabled="${noThresholds ? "true" : "false"}"
                title="${noThresholds ? "Select a threshold chip above first" : ""}">Compute delta</button>
      </div>
      <div class="rc-delta__status" data-testid="rc-delta-status"></div>
      <div class="rc-delta__chart-wrap">
        <canvas id="rc-delta-chart" data-testid="rc-delta-chart"
                aria-label="Per-month high-prob delta chart" hidden></canvas>
      </div>
      <div class="rc-delta__moved" data-testid="rc-delta-moved"></div>
    </div>
  `;
  target
    .querySelector<HTMLButtonElement>("[data-testid='rc-delta-toggle']")
    ?.addEventListener("click", () => {
      state.deltaExpanded = !state.deltaExpanded;
      _renderDeltaShell(host);
      // Match the chip-click handler's guard: only auto-fire the
      // delta when there's a band to compare against. Without this,
      // toggle-open with thresholds=[] still routes through
      // `_refreshDelta` to hit the short-circuit — harmless today
      // because the short-circuit writes the hint to status, but
      // the asymmetry is a refactor trap (code-review HIGH).
      if (
        state.deltaExpanded &&
        !state.deltaResult &&
        !state.deltaLoading &&
        state.thresholds.length > 0
      ) {
        void _refreshDelta(host);
      }
    });
  // Pre-PR code-review MED: use `input` instead of `change` so mid-
  // edit keystroke values sync to module state. Otherwise, an operator
  // typing a new date and clicking "Compute delta" before the input
  // blurs sends the previous value to the BE.
  target.querySelector<HTMLInputElement>("[data-testid='rc-delta-from']")?.addEventListener(
    "input",
    (ev) => {
      const v = (ev.target as HTMLInputElement).value;
      if (v) state.deltaFrom = v;
    },
  );
  target.querySelector<HTMLInputElement>("[data-testid='rc-delta-to']")?.addEventListener(
    "input",
    (ev) => {
      const v = (ev.target as HTMLInputElement).value;
      if (v) state.deltaTo = v;
    },
  );
  target
    .querySelector<HTMLButtonElement>("[data-testid='rc-delta-run']")
    ?.addEventListener("click", () => {
      void _refreshDelta(host);
    });

  // Re-render existing result if any. The shell's `innerHTML` write
  // above must have completed before this — the result + error
  // renderers write into `[data-testid='rc-delta-status']` / chart
  // canvas / moved table, which only exist after the shell write.
  // Architect LOW: keep this block AFTER the shell write.
  if (state.deltaResult) {
    _renderDeltaResult(host, state.deltaResult);
  } else if (state.deltaError) {
    _renderDeltaError(host);
  }
}

async function _refreshDelta(host: HTMLElement): Promise<void> {
  // Pre-PR architect MED: monotonic counter so rapid "Compute delta"
  // clicks or a year-change-while-in-flight don't let a stale response
  // commit. Same pattern as `_refreshProjection`.
  const seq = ++_deltaSeq;
  // No thresholds → the BE returns all-zero deltas (ITB-only path).
  // Skip the fetch entirely so the user doesn't see a misleading
  // "$0 net movement" status, and surface the same hint as the
  // inline banner above the controls.
  if (state.thresholds.length === 0) {
    state.deltaLoading = false;
    state.deltaResult = null;
    state.deltaError = "Select one or more threshold chips above to compare snapshots.";
    _renderDeltaError(host);
    return;
  }
  state.deltaLoading = true;
  state.deltaError = null;
  state.deltaResult = null;
  _renderDeltaLoading(host);

  // Client-side date validation — span > 366d is a 422 from the BE.
  if (state.deltaFrom > state.deltaTo) {
    if (seq !== _deltaSeq) return;
    state.deltaLoading = false;
    state.deltaError = "From date must be on or before To date.";
    _renderDeltaError(host);
    return;
  }
  try {
    const r = await fetchRevenueProjectionDelta({
      fromDate: state.deltaFrom,
      toDate: state.deltaTo,
      year: state.year,
      thresholds: state.thresholds,
      excludePassThrough: state.excludePassThrough,
    });
    if (seq !== _deltaSeq) return;
    state.deltaLoading = false;
    state.deltaResult = r;
    _renderDeltaResult(host, r);
  } catch (e) {
    if (seq !== _deltaSeq) return;
    state.deltaLoading = false;
    state.deltaError = e instanceof Error ? e.message : String(e);
    _renderDeltaError(host);
  }
}

function _renderDeltaLoading(host: HTMLElement): void {
  const status = host.querySelector<HTMLElement>("[data-testid='rc-delta-status']");
  if (status) status.textContent = "Loading delta…";
}
function _renderDeltaError(host: HTMLElement): void {
  const status = host.querySelector<HTMLElement>("[data-testid='rc-delta-status']");
  if (status) status.textContent = state.deltaError ?? "";
}

function _renderDeltaResult(host: HTMLElement, r: RevenueProjectionDeltaResponse): void {
  const status = host.querySelector<HTMLElement>("[data-testid='rc-delta-status']");
  if (status) {
    status.textContent = `${r.from_date} → ${r.to_date}: ${fmtMoney(r.high_prob_delta_total)} net high-prob movement (ITB unchanged).`;
  }
  _drawDeltaChart(r);
  _renderWhatMoved(host, r);
}

function _drawDeltaChart(r: RevenueProjectionDeltaResponse): void {
  const canvas = document.getElementById("rc-delta-chart");
  if (!(canvas instanceof HTMLCanvasElement)) return;
  canvas.hidden = false;
  _deltaChart?.destroy();
  const labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const byMonth = new Map(r.high_prob_delta_by_month.map((m) => [m.month, m.amount_delta]));
  const data = Array.from({ length: 12 }, (_, i) => byMonth.get(i + 1) ?? 0);
  _deltaChart = new Chart(canvas, {
    type: "bar",
    data: {
      labels,
      datasets: [
        {
          label: "Net high-prob delta",
          data,
          backgroundColor: data.map((v) => (v >= 0 ? "#2bb673" : "#d9534f")),
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        title: { display: true, text: `High-prob delta · ${r.year}` },
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (c) => `${c.dataset.label}: ${fmtMoney(c.parsed.y)}`,
          },
        },
      },
      scales: {
        y: {
          ticks: {
            callback: (v) => {
              const n = typeof v === "string" ? Number(v) : v;
              return Number.isFinite(n) ? fmtMoney(n) : String(v);
            },
          },
        },
      },
    },
  });
}

function _renderWhatMoved(host: HTMLElement, r: RevenueProjectionDeltaResponse): void {
  const target = host.querySelector<HTMLElement>("[data-testid='rc-delta-moved']");
  if (!target) return;
  if (r.what_moved.length === 0) {
    target.innerHTML = `<p class="rc-delta__empty">No opportunities moved in or out of the selected buckets.</p>`;
    return;
  }
  // Already sorted by abs(amount_delta) desc server-side; preserve.
  target.innerHTML = `
    <h4 class="rc-delta__moved-title">What moved</h4>
    <table class="rc-delta__moved-table" data-testid="rc-delta-moved-table">
      <thead>
        <tr>
          <th scope="col">Opportunity</th>
          <th scope="col">Reason</th>
          <th scope="col" class="rc-num">Δ</th>
        </tr>
      </thead>
      <tbody>
        ${r.what_moved
          .map(
            (w) => `
          <tr data-opp="${esc(w.opportunity_id)}">
            <td>${esc(w.name)}</td>
            <td>${esc(w.reason)}</td>
            <td class="rc-num ${w.amount_delta > 0 ? "rc-num--pos" : w.amount_delta < 0 ? "rc-num--neg" : ""}">
              ${esc(fmtMoney(w.amount_delta))}
            </td>
          </tr>`,
          )
          .join("")}
      </tbody>
    </table>
  `;
}

// ---------------------------------------------------------------------------
// Test seams — re-export the module state so vitest tests can assert
// directly on chip / year flips without relying on DOM walks alone.
// ---------------------------------------------------------------------------

export function _getCalendarState(): Readonly<CalendarState> {
  return state;
}

// ---------------------------------------------------------------------------
// SGNL-246 — 3-bucket revenue pacing summary (new section at top of page)
//
// Calls /api/revenue-pacing which applies the admin-managed pass-through
// exclusion filter and surfaces ITB + Qualified factored + Unqualified
// + derived Visibility / Go-Get / Pipeline Coverage in one round trip.
// See docs/sgnl-244-itb-reconciliation.md for why this section exists
// and what makes its numbers different from the older threshold-driven
// projection block below.
// ---------------------------------------------------------------------------

// Sequence-counter dedicated to pacing refetches. Same monotonic-fetch
// guard pattern as the projection / delta panels — a fast year change
// must not let an in-flight stale response commit over a fresh one.
let _pacingSeq = 0;

async function _refreshPacing(host: HTMLElement): Promise<void> {
  const slot = host.querySelector<HTMLElement>("[data-testid='rc-pacing']");
  if (!slot) return;

  const seq = ++_pacingSeq;
  state.pacingLoading = true;
  state.pacingError = null;
  // PR-Agent importance 6: wipe stale data while a fresh fetch is in
  // flight. Without this, a year-change leaves the previous year's
  // bucket totals on screen — looks like valid current-year data but
  // it's last-year's snapshot.
  state.pacing = null;
  _renderPacing(host);

  try {
    const result = await fetchRevenuePacing({ year: state.year });
    if (seq !== _pacingSeq) return; // stale
    state.pacing = result;
  } catch (err) {
    if (seq !== _pacingSeq) return;
    state.pacing = null;
    state.pacingError =
      err instanceof ApiError ? `${err.message} (HTTP ${err.status})` : String(err);
  } finally {
    if (seq === _pacingSeq) {
      state.pacingLoading = false;
      _renderPacing(host);
    }
  }
}

function _renderPacing(host: HTMLElement): void {
  const slot = host.querySelector<HTMLElement>("[data-testid='rc-pacing']");
  if (!slot) return;
  if (state.pacingLoading && !state.pacing) {
    slot.innerHTML = `
      <div class="rc-pacing rc-pacing--loading" data-testid="rc-pacing-loading">
        Loading pacing summary…
      </div>`;
    return;
  }
  if (state.pacingError) {
    slot.innerHTML = `
      <div class="rc-pacing rc-pacing--error" data-testid="rc-pacing-error">
        Couldn't load pacing summary: ${esc(state.pacingError)}
      </div>`;
    return;
  }
  const p = state.pacing;
  if (!p) {
    slot.innerHTML = "";
    return;
  }

  // Excluded categories caveat — transparently surface which SF
  // RecordType.Name values were filtered out of these totals so the
  // user knows the number isn't "all of Salesforce".
  const excludedCaveat =
    p.excluded_categories.length > 0
      ? `<div class="rc-pacing__excluded" data-testid="rc-pacing-excluded">
           Excluded pass-through revenue: ${esc(p.excluded_categories.join(", "))}
         </div>`
      : "";

  const snapshotLine =
    p.snapshot_at !== null
      ? `<div class="rc-pacing__snapshot" data-testid="rc-pacing-snapshot">
           Data as of ${esc(p.snapshot_at.slice(0, 19).replace("T", " "))} UTC
         </div>`
      : "";

  const goGet =
    p.go_get !== null
      ? `<div class="rc-pacing__metric">
           <div class="rc-pacing__metric-label">Go Get</div>
           <div class="rc-pacing__metric-value rc-pacing__metric-value--coral"
                data-testid="rc-pacing-go-get">${esc(fmtMoney(p.go_get))}</div>
         </div>`
      : "";

  const coverage =
    p.pipeline_coverage !== null
      ? `<div class="rc-pacing__metric">
           <div class="rc-pacing__metric-label">
             Pipeline Coverage (target ${p.target_coverage.toFixed(1)}×)
           </div>
           <div class="rc-pacing__metric-value
                ${p.pipeline_coverage >= p.target_coverage ? "rc-pacing__metric-value--aqua" : "rc-pacing__metric-value--coral"}"
                data-testid="rc-pacing-coverage">${p.pipeline_coverage.toFixed(2)}×</div>
         </div>`
      : "";

  // Revenue Visibility promoted to a 4th bucket-row tile (was its own
  // derived-metrics row). Saves vertical space and aligns with the
  // mental model "Visibility = ITB + Qualified" since the operand
  // tiles sit immediately to its left.
  const derivedRow =
    goGet || coverage
      ? `<div class="rc-pacing__derived">${goGet}${coverage}</div>`
      : "";

  slot.innerHTML = `
    <div class="rc-pacing" data-testid="rc-pacing-content">
      <div class="rc-pacing__buckets">
        ${_bucketTile("ITB", p.itb.total, p.itb.opportunity_count, "indigo")}
        ${_bucketTile("Qualified (F)", p.qualified_factored.total, p.qualified_factored.opportunity_count, "purple")}
        ${_bucketTile("Unqualified", p.unqualified.total, p.unqualified.opportunity_count, "slate")}
        ${_visibilityTile(p.revenue_visibility)}
      </div>
      ${derivedRow}
      ${excludedCaveat}
      ${snapshotLine}
    </div>
  `;
}

/** Visibility tile — rendered alongside the 3 buckets but visually
 * distinct (electric-blue value) since it's a derived sum, not a
 * bucket from SF. Reuses the bucket-tile CSS hooks for consistent
 * sizing; the sub-label replaces opp-count with the derivation hint
 * ("ITB + Qualified (F)"). */
function _visibilityTile(visibility: number): string {
  return `
    <div class="rc-pacing__bucket rc-pacing__bucket--electric"
         data-testid="rc-pacing-bucket-visibility">
      <div class="rc-pacing__bucket-label">Revenue Visibility</div>
      <div class="rc-pacing__bucket-total"
           data-testid="rc-pacing-visibility">${esc(fmtMoney(visibility))}</div>
      <div class="rc-pacing__bucket-count">ITB + Qualified (F)</div>
    </div>
  `;
}

function _bucketTile(
  label: string,
  total: number,
  oppCount: number,
  family: "indigo" | "purple" | "slate" | "electric",
): string {
  const testid = label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");
  return `
    <div class="rc-pacing__bucket rc-pacing__bucket--${family}"
         data-testid="rc-pacing-bucket-${testid}">
      <div class="rc-pacing__bucket-label">${esc(label)}</div>
      <div class="rc-pacing__bucket-total">${esc(fmtMoney(total))}</div>
      <div class="rc-pacing__bucket-count">${oppCount} opp${oppCount === 1 ? "" : "s"}</div>
    </div>
  `;
}

// Test seam — also refresh pacing on demand without re-running the
// whole tab mount. The existing `_resetRevenueCalendarState` plus a
// `renderRevenueCalendarTab` re-invocation already covers the full
// reset path, but tests asserting pacing-section transitions can
// call this directly.
export async function _refreshPacingForTests(host: HTMLElement): Promise<void> {
  await _refreshPacing(host);
}
